import type { GeoPoint, IsoDateTime } from './common.ts';
import type { SourceRef } from './source.ts';

// ── Événements ────────────────────────────────────────────────────────────────

export type EventCategory = 'culture' | 'sport' | 'market' | 'family' | 'civic' | 'other';

export interface LocalEvent {
  readonly id: string;
  readonly title: string;
  /** Court, rédigé par nous ou autorisé : jamais la reprise d'un texte tiers protégé. */
  readonly summary?: string;
  readonly startsAt: IsoDateTime;
  readonly endsAt?: IsoDateTime;
  readonly allDay: boolean;
  readonly category: EventCategory;
  readonly venue?: { readonly name: string; readonly address?: string; readonly coordinates?: GeoPoint };
  /** Lien vers la page source (on renvoie le trafic vers l'éditeur). */
  readonly url?: string;
  readonly source: SourceRef;
}

// ── Travaux ───────────────────────────────────────────────────────────────────

export type RoadWorkImpact = 'info' | 'traffic_reduced' | 'road_closed' | 'unknown';

export interface RoadWork {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly startsAt?: IsoDateTime;
  readonly endsAt?: IsoDateTime;
  readonly impact: RoadWorkImpact;
  readonly location: { readonly label: string; readonly point?: GeoPoint };
  readonly source: SourceRef;
}

// ── Alertes ───────────────────────────────────────────────────────────────────

export type AlertKind = 'weather' | 'air_quality' | 'transport' | 'municipal' | 'other';
export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface Alert {
  readonly id: string;
  readonly kind: AlertKind;
  readonly severity: AlertSeverity;
  readonly title: string;
  readonly message?: string;
  readonly validFrom?: IsoDateTime;
  readonly validUntil?: IsoDateTime;
  readonly source: SourceRef;
}

/**
 * Actualité locale : titre, date de publication et lien vers l'article d'origine.
 * Jamais de texte ni d'image repris : le lecteur est renvoyé chez l'éditeur.
 */
export interface NewsItem {
  readonly id: string;
  readonly title: string;
  readonly publishedAt: IsoDateTime;
  readonly url: string;
  readonly source: SourceRef;
}

// ── Lieux ─────────────────────────────────────────────────────────────────────

export type PlaceCategory =
  | 'public_service'
  | 'education'
  | 'health'
  | 'sport'
  | 'culture'
  | 'park'
  | 'market'
  | 'shop'
  | 'food'
  | 'transport'
  | 'other';

export interface Place {
  readonly id: string;
  /** Segment d'URL /lieux/:slug. */
  readonly slug: string;
  readonly name: string;
  readonly category: PlaceCategory;
  readonly coordinates: GeoPoint;
  readonly address?: string;
  /** Horaires affichables, au format de la source (texte libre ou syntaxe OSM). Jamais réinterprétés. */
  readonly openingHours?: string;
  readonly website?: string;
  readonly phone?: string;
  readonly source: SourceRef;
}

// ── Signalements communautaires (V0.2+, ADR-0010) ─────────────────────────────

export type CommunityReportCategory =
  'illegal_dumping' | 'lighting' | 'roadway' | 'obstacle' | 'traffic' | 'urban_issue' | 'other';

export type CommunityReportStatus = 'pending_review' | 'published' | 'resolved' | 'rejected';

export interface CommunityReport {
  readonly id: string;
  readonly category: CommunityReportCategory;
  readonly description: string;
  /** Position du PROBLÈME, arrondie (~10 m). Jamais la position de l'auteur. */
  readonly coordinates: GeoPoint;
  readonly photoUrl?: string;
  readonly createdAt: IsoDateTime;
  readonly status: CommunityReportStatus;
  readonly statusUpdatedAt?: IsoDateTime;
}

/** Arrondi à 4 décimales (~10 m) : minimisation des données de localisation. */
export function roundReportCoordinates(point: GeoPoint): GeoPoint {
  const round = (v: number) => Math.round(v * 10_000) / 10_000;
  return { lat: round(point.lat), lon: round(point.lon) };
}

// ── Caméra locale (hors scope V0.1) ───────────────────────────────────────────

export interface LocalCamera {
  readonly id: string;
  readonly name: string;
  readonly imageUrl: string;
  readonly capturedAt: IsoDateTime;
  readonly coordinates: GeoPoint;
  readonly source: SourceRef;
}

export const PLACE_CATEGORY_LABELS: Readonly<Record<PlaceCategory, string>> = {
  public_service: 'Service public',
  education: 'Éducation',
  health: 'Santé',
  sport: 'Sport',
  culture: 'Culture',
  park: 'Parc et jardin',
  market: 'Marché',
  shop: 'Commerce',
  food: 'Alimentation',
  transport: 'Transport',
  other: 'Autre',
};
